/// <amd-module name="TFS/Diag/ActionsMonitor/ActionMonitorObjectTree" />
import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { css } from "VSSUI/Util";

export interface IActionMonitorObjectTreeProps {
    object: any;
    name: string;
    indentation: number;
    initiallyExpanded?: boolean;
    valuesAsPre?: boolean;
}

export interface IActionMonitorObjectTreeState {
    isExpanded: boolean;
}

export class ActionMonitorObjectTree extends React.Component<IActionMonitorObjectTreeProps, IActionMonitorObjectTreeState> {
    constructor(props: IActionMonitorObjectTreeProps) {
        super(props);
        this.state = { isExpanded: props.initiallyExpanded || false };
    }

    public render(): JSX.Element {
        const { name, indentation, object } = this.props;

        const { isExpanded } = this.state;

        const isObject = this._isObject(object);
        const isArray = this._isArray(object);

        const prefix = isArray ? ": [" : isObject ? ": {" : "";

        let suffix = isArray ? "]" : isObject ? "}" : "";

        if (!isExpanded && suffix) {
            suffix = "..." + suffix;
        }

        return (
            <div>
                <Icon
                    iconName="ChevronRightSmall"
                    className={css("object-tree-chevron", isExpanded && "object-tree-chevron--expanded")}
                    onClick={this._toggleExpanded}
                />
                <Link onClick={this._toggleExpanded}>{name}</Link>
                {prefix}
                {this.state.isExpanded && <div style={{ marginLeft: indentation + "em" }}>{this._renderValues()}</div>}
                {suffix}
            </div>
        );
    }

    private _renderValues(): JSX.Element[] {
        const { object, valuesAsPre } = this.props;

        const objectValues: JSX.Element[] = [];
        const scalarValues: JSX.Element[] = [];

        const renderedValues: JSX.Element[] = [];

        if (this._isObject(object)) {
            this._forEachProperty(object, (name: string, value: any) => {
                if (this._isEmptyArray(value)) {
                    scalarValues.push(<ScalarValue key={name} name={name} value="[]" />);
                } else if (this._isObject(value)) {
                    objectValues.push(
                        <ActionMonitorObjectTree
                            key={name}
                            name={name}
                            object={value}
                            indentation={this.props.indentation + 1}
                            valuesAsPre={valuesAsPre}
                        />
                    );
                } else {
                    scalarValues.push(<ScalarValue key={name} name={name} value={value} valuesAsPre={valuesAsPre} />);
                }
            });
        } else {
            scalarValues.push(<ScalarValue key={"singular"} name={""} value={object} valuesAsPre={valuesAsPre} />);
        }

        return [...scalarValues, ...objectValues];
    }

    private _isArray(value: any): boolean {
        return Array.isArray(value);
    }

    private _isEmptyArray(value: any): boolean {
        return Array.isArray(value) && value.length === 0;
    }

    private _isObject(value: any): boolean {
        return value && typeof value === "object" && value.constructor !== Date;
    }

    private _forEachProperty(object: any, fn: Function): void {
        for (const key in object) {
            if (object.hasOwnProperty(key)) {
                fn(key, object[key]);
            }
        }
    }

    private _toggleExpanded = () => {
        this.setState({ isExpanded: !this.state.isExpanded });
    };
}

class ScalarValue extends React.Component<{ name: string; value: any; valuesAsPre?: boolean }> {
    public render(): JSX.Element {
        if (this.props.valuesAsPre) {
            return (
                <div>
                    <pre>
                        {this.props.name && this.props.name + ": "} {this._toStringValue(this.props.value)}
                    </pre>
                </div>
            );
        }

        return (
            <div>
                {this.props.name && this.props.name + ": "} <b>{this._toStringValue(this.props.value)}</b>
            </div>
        );
    }

    private _toStringValue(value: any): string {
        return this._isFunction(value)
            ? "<function>"
            : value === null
            ? "<null>"
            : value === undefined
            ? "<undefined>"
            : value === ""
            ? '""'
            : value.toString();
    }

    private _isFunction(value: any): boolean {
        return typeof value === "function";
    }
}
